import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Avatar,
  CircularProgress,
  IconButton,
  Popover,
  Tooltip,
} from "@mui/material";
import {
  Bell,
  FileText,
  CreditCard,
  Users,
  Briefcase,
  UserMinus,
  Settings,
  CheckCheck,
  Clock,
  MessageCircle,
  ThumbsUp,
  Waypoints,
  CircleAlert,
  Rss,
  Newspaper,
  Icon,
} from "lucide-react";
import { useIds } from "../../hook/AdminContext";
import CloseIcon from "@mui/icons-material/Close";
import RecordNoImage from "../recordNotFound/RecordNoImage";
import {
  get_notification_list,
  read_all_notification,
  read_notification,
} from "../../DAL/Notifications/notification";
import { handle_get_time_ago } from "../../utils/Constant";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import CircularLoader from "../loader/CircularLoader";
import { bell_icon, setting_icon, ts_logo } from "../../assets";
import { s3baseUri } from "../../config/config";

export default function NotificationPanel() {
  const navigate = useNavigate();
  const {
    userInfo,
    GetAccessOfCompany,
    socket,
    updateFeedOnNotification,
    updateJobOnNotification,
    updateUsersOnNotification,
    updateInvitationListOnNotification,
    updateSentAffiliatesOnNotification,
    updateReceivedAffiliatesOnNotification,
    updatePaymentRequestOnNotification,
  } = useIds();

  const [anchorEl, setAnchorEl] = useState(null);
  const [tab, setTab] = useState("all");
  const [notifications, setNotifications] = useState([]);
  const [isloading, setIsloading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  // -----------------------------
  // 🔥 Open / Close Popover
  // -----------------------------
  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);

  // load more functionality
  const [pageNumber, setPageNumber] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const observer = useRef();

  const lastBookElementRef = useCallback(
    (node) => {
      if (isLoadingMore || isloading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && totalPages > pageNumber) {
          handleLoadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoadingMore, totalPages, pageNumber, observer]
  );

  const handleLoadMore = () => {
    console.log("callled load more");
    setIsLoadingMore(true);
    setPageNumber(pageNumber + 1);
    loadNotifications(pageNumber + 1, true);
  };

  // -----------------------------
  // 🔥 Fetch notifications by tab
  // -----------------------------
  const loadNotifications = async (page = 0, isloadingMore = false) => {
    const resp = await get_notification_list(tab, page);
    if (resp.code == 200) {
      if (isloadingMore) {
        setNotifications([...notifications, ...resp.notifications]);
      } else {
        setNotifications(resp.notifications || []);
      }
      setTotalPages(resp.total_pages || 0);
    } else {
      enqueueSnackbar(resp.message, { variant: "error" });
    }
    setIsloading(false);
    setIsLoadingMore(false);
  };

  useEffect(() => {
    if (open) {
      setIsloading(true);
      loadNotifications();
    }
    setPageNumber(0);
    setTotalPages(0);
  }, [tab, open]);

  useEffect(() => {
    setTab("all");
  }, [open]);

  // -----------------------------
  // 🔥 Socket Listener
  // -----------------------------

  const isShowAlert = (data, showAlert) => {
    if (!showAlert || !data?.title) {
      return false;
    }

    if (!userInfo.isCompanyProfile) {
      return true;
    }

    if (data.action_type == "feed" || data.action_type == "feed_comment") {
      if (
        GetAccessOfCompany("feed?.add") ||
        GetAccessOfCompany("feed?.update")
      ) {
        if (GetAccessOfCompany("feed?.global_access")) {
          return true;
        } else {
          if (data?.feed?.action_by?._id == userInfo._id) {
            return true;
          }
        }
      }
    }

    if (data.action_type == "job") {
      if (GetAccessOfCompany("job?.add") || GetAccessOfCompany("job?.update")) {
        if (GetAccessOfCompany("job?.global_access")) {
          return true;
        } else {
          if (data?.job?.action_by?._id == userInfo._id) {
            return true;
          }
        }
      }
    }

    if (data.action_type == "affiliation") {
      if (GetAccessOfCompany("manage_profile?.company_profile")) {
        return true;
      }
    }
    if (data.action_type == "invitation") {
      if (GetAccessOfCompany("manage_profile?.manage_user")) {
        return true;
      }
    }

    return false;
  };

  const handleNotificationReceive = (data, type, showAlert = true) => {
    console.log(data, "==== socket Data ====", type);
    if (isShowAlert(data, showAlert)) {
      enqueueSnackbar(data.title, { variant: "info" });
    }
    if (data.action_type == "feed" || data.action_type == "feed_comment") {
      if (data.comment) {
        updateFeedOnNotification({
          ...data.feed,
          socket_comment: data.comment,
        });
      } else if (data.deleted_comment_id) {
        updateFeedOnNotification({
          ...data.feed,
          deleted_comment_id: data.deleted_comment_id,
        });
      } else if (data.update_comment) {
        updateFeedOnNotification({
          ...data.feed,
          update_comment: data.update_comment,
        });
      } else if (data.deleted_feed_id) {
        updateFeedOnNotification({
          deleted_feed_id: data.deleted_feed_id,
        });
      } else {
        updateFeedOnNotification({ ...data.feed });
      }
    }

    if (data.action_type == "job") {
      if (data.deleted_job_id) {
        updateJobOnNotification({
          deleted_job_id: data.deleted_job_id,
        });
      } else {
        updateJobOnNotification({ ...data.job });
      }
    }
    if (data.action_type == "payment_request") {
      if (data.metadata.deleted_payment_request_id) {
        updatePaymentRequestOnNotification({
          deleted_payment_request_id: data.metadata.deleted_payment_request_id,
        });
      } else if (type == "payment_request_updated") {
        updatePaymentRequestOnNotification({
          update_payment_request: data.metadata.payment_request,
        });
      } else {
        updatePaymentRequestOnNotification({
          ...data.metadata.payment_request,
        });
      }
    }

    if (data.notification_type == "affiliation_request_received") {
      updateReceivedAffiliatesOnNotification({
        ...data?.affiliation,
      });
    }
    if (data.notification_type == "affiliation_request_rejected") {
      updateReceivedAffiliatesOnNotification({
        deleted_affiliate_id: data?.affiliation?._id,
      });
      updateSentAffiliatesOnNotification({
        deleted_affiliate_id: data?.affiliation?._id,
      });
    }
    if (data.notification_type == "affiliation_request_accepted") {
      updateSentAffiliatesOnNotification({
        ...data?.affiliation,
      });
    }
    if (data.notification_type == "user_invitation_received") {
      updateInvitationListOnNotification(data.invitation);
    }
    if (data.notification_type == "invitation_updated") {
      if (data?.deleted_invitation_id) {
        updateInvitationListOnNotification({
          deleted_invitation_id: data?.deleted_invitation_id,
        });
      } else {
        updateInvitationListOnNotification({
          update_invitation: data.invitation,
        });
      }
    }

    if (
      data.notification_type == "user_invitation_accepted" ||
      data.notification_type == "user_invitation_rejected"
    ) {
      updateUsersOnNotification(data.invitation);
    }
  };
  useEffect(() => {
    if (!socket) return;
    socket.on("notification_count", (data) => {
      console.log("==== socket Unread Count ====", data.count);
      setUnreadCount(data.count);
    });
    socket.on("update_notification_count", (data) => {
      // setUnreadCount(data.count);
      socket.emit("get_notification_count", {
        user_id: userInfo.profile_id,
        token: localStorage.getItem("token"),
      });
    });

    socket.on("job_application_received", (data) => {
      handleNotificationReceive(data, "job_application_received");
    });
    socket.on("job_application_accepted", (data) => {
      handleNotificationReceive(data, "job_application_accepted");
    });
    socket.on("job_application_rejected", (data) => {
      handleNotificationReceive(data, "job_application_rejected");
    });
    socket.on("job_updated", (data) => {
      handleNotificationReceive(data, "job_updated", false);
    });

    socket.on("feed_like", (data) => {
      handleNotificationReceive(data, "feed_like");
    });
    socket.on("feed_comment_add", (data) => {
      handleNotificationReceive(data, "feed_comment_add");
    });
    socket.on("feed_comment_like", (data) => {
      handleNotificationReceive(data, "feed_comment_like");
    });
    socket.on("feed_updated", (data) => {
      handleNotificationReceive(data, "feed_updated", false);
    });

    socket.on("payment_request_received", (data) => {
      handleNotificationReceive(data, "payment_request_received");
    });
    socket.on("payment_request_updated", (data) => {
      handleNotificationReceive(data, "payment_request_updated", false);
    });
    // socket.on("subscription_failed", (data) => {
    //   handleNotificationReceive(data, "subscription_failed");
    // });

    socket.on("affiliation_request_received", (data) => {
      handleNotificationReceive(data, "affiliation_request_received");
    });
    socket.on("affiliation_request_accepted", (data) => {
      handleNotificationReceive(data, "affiliation_request_accepted");
    });
    socket.on("affiliation_request_rejected", (data) => {
      handleNotificationReceive(data, "affiliation_request_rejected");
    });

    socket.on("user_invitation_accepted", (data) => {
      handleNotificationReceive(data, "user_invitation_accepted");
    });
    socket.on("user_invitation_rejected", (data) => {
      handleNotificationReceive(data, "user_invitation_rejected");
    });
    socket.on("user_invitation_received", (data) => {
      handleNotificationReceive(data, "user_invitation_received");
    });
    socket.on("invitation_updated", (data) => {
      handleNotificationReceive(data, "invitation_updated");
    });

    return () => {
      socket.off("notification_count");
      socket.off("update_notification_count");

      socket.off("job_application_received");
      socket.off("job_application_accepted");
      socket.off("job_application_rejected");
      socket.off("job_updated");

      socket.off("feed_like");
      socket.off("feed_comment_add");
      socket.off("feed_comment_like");
      socket.off("feed_updated");

      socket.off("payment_request_received");
      socket.off("payment_request_updated");
      // socket.off("subscription_failed");

      socket.off("affiliation_request_received");
      socket.off("affiliation_request_accepted");
      socket.off("affiliation_request_rejected");

      socket.off("user_invitation_received");
      socket.off("user_invitation_accepted");
      socket.off("user_invitation_rejected");
      socket.off("invitation_updated");
    };
  }, [socket]);

  // -----------------------------
  // Functions
  // -----------------------------
  const handleMarkReadNotification = async (item) => {
    try {
      if (!item?._id) return;

      const resp = await read_notification(item._id);

      if (resp?.code === 200) {
        setNotifications((prev) =>
          prev.map((obj) =>
            obj._id === item._id ? { ...obj, is_read: true } : obj
          )
        );
        setUnreadCount((prev) => Math.max(prev - 1, 0));
      } else {
        console.warn("Failed to mark as read!", resp);
      }
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  const handleMarkAllAsRead = async () => {
    const resp = await read_all_notification();
    if (resp.code == 200) {
      setNotifications((prev) =>
        prev.map((obj) => ({ ...obj, is_read: true }))
      );
      setUnreadCount(0);
    } else {
      enqueueSnackbar(resp.message, { variant: "error" });
    }
  };

  const handleNavigate = async (item) => {
    if (!item.is_read) {
      handleMarkReadNotification(item);
    }

    if (
      item.notification_type == "admin_action_job_deactivated" ||
      item.notification_type == "admin_action_job_deleted" ||
      item.notification_type == "admin_action_feed_deactivated" ||
      item.notification_type == "admin_action_feed_deleted" ||
      item.notification_type == "admin_action_blog_deactivated" ||
      item.notification_type == "admin_action_blog_deleted" ||
      item.notification_type == "admin_action_feed_comment_deactivated" ||
      item.notification_type == "admin_action_feed_comment_deleted"
    ) {
      navigate("/data-not-available");
      handleClose();
      return;
    }

    if (item.action_type == "feed") {
      if (item.notification_type == "admin_action_feed_deactivated") {
        navigate(`/home?feed_id=${item.action_id}&deactivated=true`);
      } else {
        navigate(`/home?feed_id=${item.action_id}`);
      }
    }
    if (item.action_type == "feed_comment" && item?.metadata?.feed_id) {
      navigate(`/home?feed_id=${item?.metadata?.feed_id}`);
    }
    if (item.notification_type == "job_application_received") {
      navigate(
        `/${item.metadata.job_source_slug}/live-job-feed/job-candidates-page/${item.action_id}`,
        { replace: true }
      );
      return;
    }
    if (item.action_type == "job") {
      navigate(
        `/${item.metadata.job?.source?.slug}/job/${item.metadata.job.job_slug}`,
        { replace: true }
      );
    }
    if (item.action_type == "blog" && item?.metadata.blog) {
      navigate(
        `/${item?.metadata?.blog?.source?.[0]?.slug}/feature/${item?.metadata?.blog?.feature?.[0]?.slug}/insight-details/${item?.metadata?.blog?.slug}`
      );
    }
    if (item.action_type == "payment_request") {
      navigate(`/payment-requests/${item.metadata.payment_request._id}`, {
        replace: true,
      });
    }
    if (
      item.notification_type == "affiliation_request_accepted" ||
      item.notification_type == "affiliation_request_rejected"
    ) {
      navigate(`/profile/manage-affiliate/affiliate-sent`);
    }
    if (item.notification_type == "affiliation_request_received") {
      navigate(`/profile/manage-affiliate/affiliate-received`);
    }
    if (
      item.notification_type == "user_invitation_accepted" ||
      item.notification_type == "user_invitation_rejected"
    ) {
      navigate(`/manage-users`);
    }
    if (
      item.notification_type == "user_invitation_received" ||
      item.notification_type == "invitation_updated"
    ) {
      navigate(`/invitations`);
    }

    handleClose();
  };

  // -----------------------------
  // Notification Type Icons
  // -----------------------------
  const icons = {
    feed_comment_add: (
      <MessageCircle size={18} color="var(--portal-secondary-color)" />
    ),
    feed_comment: (
      <MessageCircle size={18} color="var(--portal-secondary-color)" />
    ),
    feed: <Newspaper size={18} color="var(--portal-secondary-color)" />,
    blog: <Newspaper size={18} color="var(--portal-secondary-color)" />,
    feed_like: <ThumbsUp size={18} color="var(--portal-secondary-color)" />,
    feed_comment_like: (
      <ThumbsUp size={18} color="var(--portal-secondary-color)" />
    ),
    report_submitted: (
      <CircleAlert size={18} color="var(--portal-secondary-color)" />
    ),

    payment_request_received: (
      <CreditCard size={18} color="var(--portal-secondary-color)" />
    ),
    // subscription_failed: (
    //   <CreditCard size={18} color="var(--portal-secondary-color)" />
    // ),

    affiliation_request_received: (
      <Waypoints size={18} color="var(--portal-secondary-color)" />
    ),
    affiliation_request_accepted: (
      <Waypoints size={18} color="var(--portal-secondary-color)" />
    ),
    affiliation_request_rejected: (
      <Waypoints size={18} color="var(--portal-secondary-color)" />
    ),

    user_invitation_received: (
      <Users size={18} color="var(--portal-secondary-color)" />
    ),
    user_invitation_accepted: (
      <Users size={18} color="var(--portal-secondary-color)" />
    ),
    user_invitation_rejected: (
      <UserMinus size={18} color="var(--portal-secondary-color)" />
    ),

    job: <Briefcase size={18} color="var(--portal-secondary-color)" />,
    job_application_submitted: (
      <Briefcase size={18} color="var(--portal-secondary-color)" />
    ),
    job_application_received: (
      <Briefcase size={18} color="var(--portal-secondary-color)" />
    ),
    job_application_accepted: (
      <Briefcase size={18} color="var(--portal-secondary-color)" />
    ),
    job_application_rejected: (
      <Briefcase size={18} color="var(--portal-secondary-color)" />
    ),
  };
  console.log(notifications, "notifications");
  return (
    <>
      {/* =========================================
          🔔 Notification Icon + Badge
      ========================================== */}
      {/* <div className="tw-relative tw-cursor-pointer" onClick={handleOpen}>
        <Bell size={20} className="tw-ms-2 tw-text-primary" />

        {unreadCount > 0 && (
          <span
            className="
              tw-absolute tw--top-1 tw--right-1
              tw-bg-red-500 tw-text-white tw-text-xs tw-font-semibold
              tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center
              tw-rounded-full tw-shadow
            "
          >
            {unreadCount}
          </span>
        )}
      </div> */}

      <div className="notification-icon" onClick={handleOpen}>
        <img className="hover-primary" src={bell_icon} alt="" />
        {unreadCount > 0 ? (
          <div className="unread-count-div">{unreadCount}</div>
        ) : (
          ""
        )}
      </div>

      {/* =========================================
          🔥 MUI Popover Section
      ========================================== */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          className:
            "tw-w-[407px] tw-h-[500px] tw-rounded-xl tw-shadow-xl tw-bg-white tw-flex tw-flex-col notification-modal",
        }}
      >
        {/* Header */}
        <div>
          <div className="tw-flex tw-items-center tw-justify-between pt-3 px-3">
            <h6 className="mb-0 text-lg font-medium">Notifications</h6>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          {/* Tabs */}
          <div className="tw-flex tw-gap-3 tw-px-3 tw-pt-2 tw-pb-3 tw-border-b  border-bottom-1  justify-content-between align-items-center">
            <div>
              <span
                onClick={() => setTab("all")}
                className={`notification-chip  ${
                  tab === "all" ? "notification-color" : ""
                }`}
              >
                All
              </span>
              <span
                onClick={() => setTab("unread")}
                className={`notification-chip  ${
                  tab === "unread" ? "notification-color" : ""
                }`}
              >
                Unread
              </span>
            </div>

            {unreadCount > 0 ? (
              // <Tooltip title="Mark all as read" placement="top">
              //   <CheckCheck
              //     onClick={handleMarkAllAsRead}
              //     size={18}
              //     className="tw-ml-auto cursor-pointer"
              //     style={{ color: "var(--success-color)" }}
              //   />
              // </Tooltip>
              <div className="read-text pe-2">
                <p onClick={handleMarkAllAsRead}>Mark all as read</p>
              </div>
            ) : (
              ""
            )}
          </div>
        </div>

        {/* Notifications List */}
        <div className="notification-panel tw-flex-1 tw-min-h-0 tw-overflow-y-auto">
          {isloading ? (
            <>
              <div className="tw-mt-24">
                <CircularLoader height="auto" />
              </div>
            </>
          ) : notifications.length === 0 ? (
            <div className="my-5">
              <RecordNoImage title={"No notifications found"} />
            </div>
          ) : (
            <>
              {notifications.map((item, index) => (
                <div
                  key={index}
                  className={`tw-flex tw-gap-4 tw-p-6 tw-cursor-pointer hover:tw-bg-gray-50 border-bottom-1   ${
                    !item.is_read
                      ? "tw-border-0 tw-border-gold-light tw-border-solid bg-light-gold"
                      : ""
                  }`}
                  onClick={() => {
                    handleNavigate(item);
                  }}
                >
                  <div className="tw-w-10 tw-h-10 tw-rounded-xl tw-flex flex-shrink-0">
                    {/* {icons[item.notification_type] || icons.application} */}

                    {item.type === "user" &&
                    item.notification_type !== "payment_request_received" ? (
                      <Avatar
                        src={
                          s3baseUri +
                          item?.action_by?.profile_image?.thumbnail_1
                        }
                        sx={{ width: 40, height: 40 }}
                        alt={item?.action_by?.full_name}
                      />
                    ) : (
                      <div className="position-relative">
                        <Avatar
                          src={ts_logo}
                          sx={{ width: 40, height: 40 }}
                          alt="notification-avatar"
                        />
                        <div className="notification-setting-icon">
                          <img className="w-100" src={setting_icon} alt="" />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="tw-flex tw-flex-col">
                    <h4 className="mb-0 text-xs">{item.title}</h4>
                    <span className="mt-1 text-xxs text-gray2  d-flex align-items-center">
                      <Clock
                        className="me-1 text-gray2 "
                        height={10}
                        width={10}
                      />
                      {handle_get_time_ago(item.createdAt)}
                    </span>
                  </div>
                </div>
              ))}

              {!isloading &&
                totalPages > 1 &&
                totalPages > pageNumber &&
                notifications.length > 0 && (
                  <div className="col-12 text-center mt-3">
                    <div
                      ref={lastBookElementRef}
                      className="mt-3"
                      onClick={() => {
                        handleLoadMore();
                      }}
                      id="load-more-feed"
                    >
                      {isLoadingMore ? (
                        <CircularProgress size={15} />
                      ) : (
                        "Load More"
                      )}
                    </div>
                  </div>
                )}
            </>
          )}
        </div>
      </Popover>
    </>
  );
}
